import { RxReplicationPullStreamItem, WithDeleted } from 'rxdb';
import { paths } from 'configuration/paths';
import { User } from 'types/user';
import { Survey } from 'types/survey';
import { SurveyResponse } from 'types/surveyResponse';
import { CustomerVisit } from 'types/customer';
import { Checkpoint, DataPull, DataPushItem } from 'types/dataPullPush';

const BACKEND_URL: string = import.meta.env.VITE_BACKEND_URL || window.runtimeConfig.BACKEND_URL;

export const get = async <T>(url: string): Promise<T> => {
  try {
    const response = await fetch(`${BACKEND_URL}/${url}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      if (response.status === 401) {
        window.location.href = getLoginUrl();
      }
      throw new Error(response.statusText);
    }

    return (await response.json()) as T;
  } catch (e) {
    // TODO: top-level error handler
    console.error(`Failed to GET ${url}`, e);
    throw e;
  }
};

export const post = async <TRequest, TResponse>(
  url: string,
  body: TRequest,
  method?: string
): Promise<TResponse | void> => {
  try {
    const response = await fetch(`${BACKEND_URL}/${url}`, {
      method: method ?? 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    if (!response.ok) {
      if (response.status === 401) {
        window.location.href = getLoginUrl();
      }
      throw new Error(response.statusText);
    }

    if (response.status === 204) {
      return;
    }
    return (await response.json()) as TResponse;
  } catch (e) {
    // TODO: top-level error handler
    console.error(`Failed to POST ${url}`, e);
    throw e;
  }
};

export const getProfile = async (): Promise<User | null> => {
  const response = await get<User>(paths.api.profile);
  if (!response.userId) {
    return null;
  }
  return response;
};

export const pullSurveyDefinitions = async (since: number): Promise<DataPull<WithDeleted<Survey>>> => {
  return await get<DataPull<WithDeleted<Survey>>>(paths.api.surveyDefinitionPull + '?since=' + since);
};

export const pullSurveyResponses = async (since: number): Promise<DataPull<SurveyResponse>> => {
  return await get<DataPull<SurveyResponse>>(paths.api.mySurveysPull + '?since=' + since);
};

export const pushSurveyResponses = async (changedRows: DataPushItem<SurveyResponse>[]): Promise<SurveyResponse[]> => {
  return await post<unknown, any>(paths.api.mySurveysPush, changedRows);
};

export const getSurveyResponse = async (id: string): Promise<SurveyResponse | null> => {
  return await get<SurveyResponse | null>(paths.api.mySurveys + '/' + id);
};

export const watchSurveyResponses = async (onEvent: (event: RxReplicationPullStreamItem<SurveyResponse, Checkpoint>) => void, onError: () => void) => {
  const eventSource = new EventSource(`${BACKEND_URL}/${paths.api.mySurveysWatch}`, {
    withCredentials: true
  });
  eventSource.onmessage = (e) => {
    const data = JSON.parse(e.data);
    onEvent(data);
  };
  eventSource.onerror = onError;
  return eventSource;
}

export const getCustomerVisits = async (): Promise<CustomerVisit[]> => {
  return await get<CustomerVisit[]>(paths.api.customerVisits);
};

export const putBlob = async (container: string, blobName: string, blob: Blob): Promise<void> => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/blob/${container}/${blobName}`, {
      method: 'PUT',
      body: blob,
      headers: {
        'Content-Type': blob.type
      },
      credentials: 'include'
    });
    if (!response.ok) {
      if (response.status === 401) {
        window.location.href = getLoginUrl();
      }
      throw new Error(response.statusText);
    }
  } catch (e) {
    // TODO: top-level error handler
    console.error(`Failed to POST blob ${container}/${blobName}`, e);
    throw e;
  }
};

// this simply calls a fixed endpoint (that is supposed to just return 204), with a 5 second timeout
// and returns false if it failed (will not throw)
export const ping = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/ping`, {
      credentials: 'include',
      signal: AbortSignal.timeout(5000)
    });
    return response.ok;
  } catch (e) {
    return false;
  }
};

export const getLoginUrl = (redirectUrl?: string | null) =>
  `${BACKEND_URL}/login?returnUrl=${redirectUrl ? window.location.origin + redirectUrl : window.location.href}`;
export const getLogoutUrl = () => `${BACKEND_URL}/logout?returnUrl=${window.location.origin}`;
